import { Component, effect, ElementRef, inject, Input, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ChildNotificationInterface, ItemCommunicationService, ParentNotificationInterface, ParentTriggerInterface } from 'src/app/features/items/components/items-module/items-tab/services/item-communication.service';
import { ItemStockFormService, RequireMinLength } from './item-stock-form.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Items, ItemsBomComponents, Stock } from 'tilby-models';
import { ItemBomComponentsFields, ItemStockFormGroups } from './item-stock.model';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { Subject, Subscription } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ConfigurationManagerService, EntityManagerService } from 'src/app/core';
import { MatRadioModule } from '@angular/material/radio';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { MatSelectModule } from '@angular/material/select';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { ListSelectorDialogService } from 'src/app/dialogs/list-selector-dialog/list-selector-dialog.component';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';

@Component({
  selector: 'app-item-stock',
  standalone: true,
  imports: [MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSlideToggleModule,
    MatIconModule, MatButtonModule, MatRadioModule,
    MatExpansionModule, MatSelectModule, TrimInputDirective,
    TruncatedTextDirective, MatTooltipModule
  ],
  templateUrl: './item-stock.component.html',
  styleUrl: './item-stock.component.scss'
})
export class ItemStockComponent {

  private readonly itemCommunicationService = inject(ItemCommunicationService);
  readonly itemStockFormService = inject(ItemStockFormService);
  private readonly checkManagerService = inject(ConfigurationManagerService);
  private readonly entityManagerService = inject(EntityManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);  
  private readonly translate = inject(TranslateService);
  private readonly listSelectorDialogService = inject(ListSelectorDialogService);
  private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translateService = inject(TranslateService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  
  public readonly Validators = Validators; 
  
  @Input() item!: Items;
  @Input() itemStock!: Stock;

  componentTypeList: Array<any> = [];
  subComponentTypeList: Array<any> = [];

  allRawMaterials = new Set<number | string>();
  allItems = new Set<number | string>();

  isStockAvailable = this.checkManagerService.isModuleEnabled('stock');
  combinationsActive = false;

  itemStockForm!: ItemStockFormGroups;

  private componentTypeSubscriptions = new Map<any, Subscription>();

  @ViewChild('componentAccordion') accordion: MatAccordion | undefined;
  @ViewChildren('subComponentAccordion') innerAccordion!: QueryList<MatAccordion>; 
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;

  // ViewChildren per gestire lo scroll dei pannelli delle varianti (sia interni che esterni)
  @ViewChildren(MatExpansionPanel, { read: ElementRef }) elementRefPanels!:  QueryList<ElementRef>;

  private destroy$ = new Subject<void>(); 
  isDataListReady = this.itemDataCatalogService.isReady;
  stockFormReady = this.itemStockFormService.stockFormReady;
  
  constructor() {
    this.readData();

    effect(() => {
      if(this.isDataListReady()) {
        this.itemStockForm = this.itemStockFormService.createForm(this.item);
        this.initCombinationsSet(this.itemStockFormService.bomComponentsArray?.value as ItemsBomComponents[] || []);
      }
    }, { allowSignalWrites: true })

    effect(() => {
      // Listener per eseguire operazioni richieste dal parent
      const trigger = this.itemCommunicationService.parentTrigger();
      if(trigger?.child === 'stock' || trigger?.child === 'all') {
        this.handleParentTrigger(trigger);
      }
    })

    effect(() => {
      // Listener per leggere notifiche inviate dal parent
      const notification = this.itemCommunicationService.parentNotifier();
      if (notification?.child === 'stock') {
        this.handleParentNotification(notification);
      }
    })

  }

  handleParentTrigger(message: ParentTriggerInterface) {
    // console.log("handleParentTrigger ", message)
    if (message.trigger === 'tabActivated') {
      this.loadLazyData();
    }

    if (message.trigger === 'requestStockData') {
      if (!this.stockFormReady()) return;
      
      const requestedField = message.params?.formField;

      let dataToReturn = null;
      if (requestedField && this.itemStockForm.get(requestedField)) {
        dataToReturn = this.itemStockForm.get(requestedField)?.value;
      } 

      this.sendNotificationToParent({
        notification: 'stockData',
        receiver: message.sender,
        params: { [requestedField]: dataToReturn }
      });
    }
    
    if(message.trigger === 'cancel') {
      this.itemStockFormService.resetFormValues(this.item);
    }
  }

  handleParentNotification(message: ParentNotificationInterface) {
    // console.log("handleParentNotification ", message)
    if(message.notification === 'variationData') {
      this.combinationsActive = message.params?.combinationsActive || false;
    }
  }

  ngOnInit(): void {
  }

  initCombinationsSet(bomComponents: Partial<ItemBomComponentsFields>[]): void {
    for(const component of (bomComponents || [])) {
      switch (component.type) {
        case 'raw_material':
          if(component.component_raw_material_id) {
            this.allRawMaterials.add(component.component_raw_material_id);
          }
          break;

        case 'item':
          if(component.component_item_id) {
            this.allItems.add(component.component_item_id);
          }
          break;

        case 'bom_component':
          if (component.bom_components?.length) {
            return this.initCombinationsSet(component.bom_components);
          }
          break;
      }
    }
  }

  // private updateComponentTypeSubscriptions(): void {
  //   const currentControls = new Set(this.itemStockFormService.bomComponentsArray?.controls || []);

  //   // Rimozione delle sottoscrizioni per i controlli che sono stati rimossi dal FormArray
  //   this.componentTypeSubscriptions.forEach((sub, control) => {
  //     if (!currentControls.has(control)) {
  //       sub.unsubscribe();
  //       this.componentTypeSubscriptions.delete(control);
  //     }
  //   });

  //   // Aggiunta nuove sottoscrizioni per i controlli aggiunti o esistenti non ancora sottoscritti
  //   this.itemStockFormService.bomComponentsArray?.controls.forEach((componentFormGroup: any, componentIndex: number) => {
  //     if (!this.componentTypeSubscriptions.has(componentFormGroup)) {
  //       const typeControl = componentFormGroup.get('type');
  //       if (typeControl) {
  //         const sub = typeControl.valueChanges
  //           .pipe(takeUntil(this.destroy$))
  //           .subscribe(() => {
  //           });
  //         this.componentTypeSubscriptions.set(componentFormGroup, sub);
  //       }
  //     }
  //   });
  // }
  
  loadLazyData() {
    this.requestVariationFormData();
  }

  async readData() {
    this.buildComponentTypeList();
    this.buildSubComponentTypeList();
  }

  buildComponentTypeList() {
    this.componentTypeList = [{
        name: this.translate.instant('ITEMS.DETAILS.ITEM'),
        value: 'item'
    }, {
        name: this.translate.instant('ITEMS.DETAILS.RAW_MATERIAL'),
        value: 'raw_material'
    }, {
        name: this.translate.instant('ITEMS.DETAILS.BOM_COMPONENT'),
        value: 'bom_component'
    }]
  }

  buildSubComponentTypeList() {
    this.subComponentTypeList = [{
        name: this.translate.instant('ITEMS.DETAILS.ITEM'),
        value: 'item'
    }, {
        name: this.translate.instant('ITEMS.DETAILS.RAW_MATERIAL'),
        value: 'raw_material'
    }]
  }

  getDescriptionTypeComponent(valueType: string) {
    return this.componentTypeList.find(t => t.value === valueType)?.name || '';
  }

  get itemStockLabel(): string {
    const { stock_quantity } = this.itemStock || {};
    
    if (this.combinationsActive || stock_quantity == null) {
        return '';
    }

    const unitSuffix = this.item?.unit ? ` ${this.item.unit}` : '';
    return `(${stock_quantity}${unitSuffix})`;
  }

  requestVariationFormData() {
    setTimeout(() => {
      this.itemCommunicationService.createParentTrigger('requestVariationData', 'variations', 'stock', { formField: 'combinationsActive' })
    })
  }

  onEnableStock() {
    this.itemStockFormService.auto_unload?.setValue(this.itemStockFormService.is_stock_active?.value);
    if(this.isStockAvailable && !this.itemStockFormService.stock_type?.value) {
      this.itemStockFormService.stock_type?.setValue('simple');
    }
  }

  async changeStockType() {
    if(this.itemStockFormService.stock_type?.value === 'bom') {
      var x_query = 'bom_components.component_item_id=' + this.item?.id;
      var query = {
          x_query: x_query
      };

      this.entityManagerService.items.fetchCollectionOnline(query).then((results) => {
          var isInOtherBoms;

          if (results) {
              if (!Array.isArray(results) && results.results) {
                isInOtherBoms = (results.total > 0);
              }
              isInOtherBoms = ((results as any).length > 0);
          }

          if(isInOtherBoms) {
            this.confirmDialogStockTypeIsASubComponent();
          }
      });
    } else {
      if (this.itemStockFormService.bomComponentsArray?.value?.length) {
        this.confirmDialogStockTypeDeleteBomComponent();
      }
    }
  }

  async confirmDialogStockTypeIsASubComponent() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.DETAILS.WARNING_STOCK_TYPE_IS_A_SUB_BOM_COMPONENT',
      }
    });

    if(!confirm) {
      this.itemStockFormService.stock_type?.setValue('simple');
    }
  }

  async confirmDialogStockTypeDeleteBomComponent() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.DETAILS.WARNING_STOCK_TYPE_DELETE_BOM_COMPONENT',
      }
    });

    if(confirm) {
      this.itemStockFormService.removeAllComponent();
    } else {
      this.itemStockFormService.stock_type?.setValue('bom');
    }
  }

  addNewBomComponent() {
    this.itemStockFormService.addNewComponents();

    const panelId = this.itemStockFormService.bomComponentsArray?.value?.length;
    this.scrollToPanel(panelId? panelId - 1: 0);
  }

  removeComponent(index: number) {
    this.itemStockFormService.removeComponent(index);
    this.updateOnBomChange();
  }

  addNewSubBomComponent(componentIndex: number) {
    this.itemStockFormService.addSubComponent(componentIndex);

    const innerPanelId = this.itemStockFormService.getSubComponentArray(componentIndex)?.value?.length;
    this.scrollToPanel(componentIndex, innerPanelId? innerPanelId - 1: 0);
  }

  removeSubComponent(componentIndex: number, subComponentIndex: number) {
    this.itemStockFormService.removeSubComponent(componentIndex, subComponentIndex);
    this.updateOnBomChange();
  }

  onBomComponentTypeChange(componentIndex: number) {
    this.itemStockFormService.getCostComponent(componentIndex)?.setValue(null);
    this.itemStockFormService.getUnitComponent(componentIndex)?.setValue(null);

    if(this.itemStockFormService.getNameComponent(componentIndex)?.value) {
      this.itemStockFormService.getNameComponent(componentIndex)?.setValue(null);
    }
    if(this.itemStockFormService.getQuantityComponent(componentIndex)?.value) {
      this.itemStockFormService.getQuantityComponent(componentIndex)?.setValue(null);
      this.itemStockFormService.getQuantityComponent(componentIndex)?.markAsUntouched();
    }
    if(this.itemStockFormService.getSubComponentArray(componentIndex)?.value?.length) {
      this.itemStockFormService.removeAllSubComponent(componentIndex);
    }

    if(this.itemStockFormService.getTypeComponent(componentIndex)?.value === "bom_component") {
      this.itemStockFormService.getSubComponentArray(componentIndex)?.setValidators(RequireMinLength(1));
    } else {
      this.itemStockFormService.getSubComponentArray(componentIndex)?.clearValidators();
    }
    this.itemStockFormService.getSubComponentArray(componentIndex)?.updateValueAndValidity();
    this.updateOnBomChange();
  }

  onSubBomComponentTypeChange(componentIndex: number, subComponentIndex: number) {
    if(this.itemStockFormService.getNameSubComponent(componentIndex, subComponentIndex)?.value) {
      this.itemStockFormService.getNameSubComponent(componentIndex, subComponentIndex)?.setValue(null);
    }
    if(this.itemStockFormService.getQuantitySubComponent(componentIndex, subComponentIndex)?.value) {
      this.itemStockFormService.getQuantitySubComponent(componentIndex, subComponentIndex)?.setValue(null);
    }
    this.updateOnBomChange();
  }
  
  editBomComponent(componentIndex: number) {
    const typeComponent = this.itemStockFormService.getTypeComponent(componentIndex)?.value;

    const idToFind = (typeComponent === 'item') 
      ? this.itemStockFormService.getItemIdComponent(componentIndex)?.value 
      : this.itemStockFormService.getRawMaterialIdComponent(componentIndex)?.value;

    let dataSet;
    let linkedItem;

    if (typeComponent === 'item') {
      dataSet = this.itemStockFormService.bomItems;
      linkedItem = idToFind ? this.itemStockFormService.bomItemsMap.get(idToFind) : null;
    } else if (typeComponent === 'raw_material') {
      dataSet = this.itemStockFormService.bomRawMaterials;
      linkedItem = idToFind ? this.itemStockFormService.bomRawMaterialsMap.get(idToFind) : null;
    } else {
      return;
    }

    this.listSelectorDialogService.openDialog({
        data: {
          title: "DIALOG.LIST_SELECTOR.TITLE.ITEM",
          optionList: dataSet, 
          optionValue: linkedItem
        }
      }
    ).then((res: any) => {
      if(res) {
        const isUnique = this.isNewComponentUnique(
          this.itemStockFormService.bomComponentsArray?.value || [], 
          res.id,
          typeComponent,
          componentIndex
        );
        if(!isUnique) {
          this.alertDialogService.openDialog({
            data: {
              messageLabel: this.translate.instant('ITEMS.DETAILS.BOM_COMPONENT_WITH_REPEATED_VALUES')
            }
          })
          this.itemStockFormService.getItemIdComponent(componentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getRawMaterialIdComponent(componentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getCostComponent(componentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getUnitComponent(componentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getNameComponent(componentIndex)?.setValue(null, {emitEvent: false});
          return;
        }
        if (typeComponent === 'item') {
          this.itemStockFormService.getItemIdComponent(componentIndex)?.setValue(res.id);
          const item = this.itemStockFormService.bomItemsMap.get(res.id);
          if(item) {
            const itemCost = item.cost? this.tilbyCurrencyPipe.transform(item.cost): null;
            this.itemStockFormService.getCostComponent(componentIndex)?.setValue(itemCost);
            this.itemStockFormService.getUnitComponent(componentIndex)?.setValue(item.unit);
          }
        } else if (typeComponent === 'raw_material') {
          this.itemStockFormService.getRawMaterialIdComponent(componentIndex)?.setValue(res.id);
          const material = this.itemStockFormService.bomRawMaterialsMap.get(res.id);
          if(material) {
            const materialCost = material.cost? this.tilbyCurrencyPipe.transform(material.cost): null;
            this.itemStockFormService.getCostComponent(componentIndex)?.setValue(materialCost);
            this.itemStockFormService.getUnitComponent(componentIndex)?.setValue(material.unit);
          }
        }
        this.itemStockFormService.getNameComponent(componentIndex)?.setValue(res.name);
      }
      this.updateOnBomChange();
    })
  }

  editSubBomComponent(componentIndex: number, subComponentIndex: number) {
    const typeComponent = this.itemStockFormService.getTypeSubComponent(componentIndex, subComponentIndex)?.value;

    const idToFind = (typeComponent === 'item') 
      ? this.itemStockFormService.getItemIdSubComponent(componentIndex, subComponentIndex)?.value 
      : this.itemStockFormService.getRawMaterialIdSubComponent(componentIndex, subComponentIndex)?.value;

    let dataSet;
    let linkedItem;

    if (typeComponent === 'item') {
      dataSet = this.itemStockFormService.bomItems;
      linkedItem = idToFind ? this.itemStockFormService.bomItemsMap.get(idToFind) : null;
    } else if (typeComponent === 'raw_material') {
      dataSet = this.itemStockFormService.bomRawMaterials;
      linkedItem = idToFind ? this.itemStockFormService.bomRawMaterialsMap.get(idToFind) : null;
    } else {
      return;
    }

    this.listSelectorDialogService.openDialog({
        data: {
          title: "DIALOG.LIST_SELECTOR.TITLE.ITEM",
          optionList: dataSet, 
          optionValue: linkedItem
        }
      }
    ).then((res: any) => {
      if(res) {
        const isUnique = this.isNewComponentUnique(
          this.itemStockFormService.getSubComponentArray(componentIndex)?.value || [], 
          res.id,
          typeComponent,
          subComponentIndex
        );
        if(!isUnique) {
          this.alertDialogService.openDialog({
            data: {
              messageLabel: this.translate.instant('ITEMS.DETAILS.BOM_COMPONENT_WITH_REPEATED_VALUES')
            }
          })

          this.itemStockFormService.getItemIdSubComponent(componentIndex, subComponentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getRawMaterialIdSubComponent(componentIndex, subComponentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getCostSubComponent(componentIndex, subComponentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getUnitSubComponent(componentIndex, subComponentIndex)?.setValue(null, {emitEvent: false});
          this.itemStockFormService.getNameSubComponent(componentIndex, subComponentIndex)?.setValue(null, {emitEvent: false});
          return;
        }
        if (typeComponent === 'item') {
          this.itemStockFormService.getItemIdSubComponent(componentIndex, subComponentIndex)?.setValue(res.id);
          const item = this.itemStockFormService.bomItemsMap.get(res.id);
          if(item) {
            const itemCost = item.cost? this.tilbyCurrencyPipe.transform(item.cost): null;
            this.itemStockFormService.getCostSubComponent(componentIndex, subComponentIndex)?.setValue(itemCost);
            this.itemStockFormService.getUnitSubComponent(componentIndex, subComponentIndex)?.setValue(item.unit);
          }
        } else if (typeComponent === 'raw_material') {
          this.itemStockFormService.getRawMaterialIdSubComponent(componentIndex, subComponentIndex)?.setValue(res.id);
          const material = this.itemStockFormService.bomRawMaterialsMap.get(res.id);
          if(material) {
            const materialCost = material.cost? this.tilbyCurrencyPipe.transform(material.cost): null;
            this.itemStockFormService.getCostSubComponent(componentIndex, subComponentIndex)?.setValue(materialCost);
            this.itemStockFormService.getUnitSubComponent(componentIndex, subComponentIndex)?.setValue(material.unit);
          }
        }
        this.itemStockFormService.getNameSubComponent(componentIndex, subComponentIndex)?.setValue(res.name);
      }
      this.updateOnBomChange();
    })
  }

  updateOnBomChange() {
    const bomComponents = this.itemStockFormService.bomComponentsArray? this.itemStockFormService.bomComponentsArray.value as ItemBomComponentsFields[] : [];
    const itemCost = this.itemStockFormService.calculateItemCost(bomComponents);
    this.sendNotificationToParent({
      notification: 'stockCost',
      receiver: 'general',
      params: itemCost
    })
  }

  isNewComponentUnique(
    bomComponents: any[], 
    newComponentId: number, 
    newComponentType: 'item' | 'raw_material',
    currentIndex: number
  ): boolean {

    if (newComponentType === 'raw_material') {
      const rmIds = new Set(
        bomComponents
          .filter((c, index) => index !== currentIndex && c.type === 'raw_material')
          .map(c => c.component_raw_material_id)
      );
      return !rmIds.has(newComponentId);
    }

    if (newComponentType === 'item') {
      const itemIds = new Set(
        bomComponents
          .filter((c, index) => index !== currentIndex && c.type === 'item')
          .map(c => c.component_item_id)
      );
      return !itemIds.has(newComponentId);
    }
    return true;
  }

  sendNotificationToParent(notification: ChildNotificationInterface) {
    Promise.resolve().then(() => {
      this.itemCommunicationService.notifyParent(notification);
    });
  }

  scrollToPanel(panelId: number, innerPanelId?: number) {
    // Mi assicuro che il DOM sia aggiornato
    requestAnimationFrame(() => {
      let targetElement: HTMLElement | null = null;
      let elementToFocus: HTMLInputElement | null = null;

      if(innerPanelId != undefined) {
        this.elementRefPanels.forEach((el: ElementRef, i: number) => {
          const innerPanelContent = el.nativeElement.querySelector(`#inner-panel-content-${panelId}-${innerPanelId}`);
          // const panelContent = el.nativeElement.querySelector(`#panel-content-${panelId}`);

          if(innerPanelContent) {
            targetElement =innerPanelContent;

            if(targetElement) {
              elementToFocus = targetElement?.querySelector('mat-select[formControlName="type"]') as HTMLInputElement; 
            }
          }
        })
      } else {
        // Cerco il pannello esterno
        targetElement = this.elementRefPanels?.find(
          (el: ElementRef) => el.nativeElement.querySelector(`#panel-content-${panelId}`)
        )?.nativeElement;
        
        if(targetElement) {
          elementToFocus = targetElement?.querySelector('mat-select[formControlName="type"]') as HTMLInputElement; 
        }
      }
      
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      
      if(elementToFocus) {
        elementToFocus.focus();
      }
    })
  }

  getDescriptionComponent(index: number) {
    let description = this.getDescriptionTypeComponent(this.itemStockFormService.getTypeComponent(index)?.value) || 
      this.translateService.instant('ITEMS.DETAILS.STOCK_TYPE');
    description += ": ";
    description += "<strong>"+(this.itemStockFormService.getNameComponent(index)?.value || '-')+"</strong>";
    description += ", ";
    description += this.translateService.instant('ITEMS.DETAILS.BOM_COMPONENT_QUANTITY');
    description += ": ";
    description += "<strong>"+(this.itemStockFormService.getQuantityComponent(index)?.value || '-')+"</strong>";
    if(this.itemStockFormService.getUnitComponent(index)?.value)
      description += "<strong>"+ '\u00A0' + this.itemStockFormService.getUnitComponent(index)?.value + "</strong>";
    return description;
  }

  getDescriptionSubComponent(i: number, j: number) {
    let description = this.getDescriptionTypeComponent(this.itemStockFormService.getTypeSubComponent(i, j)?.value) || 
      this.translateService.instant('ITEMS.DETAILS.STOCK_TYPE');
    description += ": ";
    description += "<strong>"+(this.itemStockFormService.getNameSubComponent(i, j)?.value || '-')+"</strong>";
    description += ", ";
    description += this.translateService.instant('ITEMS.DETAILS.BOM_COMPONENT_QUANTITY');
    description += ": ";
    description += "<strong>"+(this.itemStockFormService.getQuantitySubComponent(i, j)?.value || '-')+"</strong>";
    if(this.itemStockFormService.getUnitSubComponent(i, j)?.value)
      description += "<strong>"+ '\u00A0' + this.itemStockFormService.getUnitSubComponent(i, j)?.value + "</strong>";
    return description;
  }

  getSafeHtml(htmlString: string | null | undefined): SafeHtml {
    if (!htmlString) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(htmlString);
  }

  getPlainText(htmlString: string | null | undefined): string {
    if (!htmlString) {
      return '';
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this.componentTypeSubscriptions.forEach(sub => sub.unsubscribe());
    this.componentTypeSubscriptions.clear();
  }

}
